// =====================================================
// MetodologiaProyeccion.js (ACTUALIZADO)
// Panel de ayuda metodológica para Proyección Crediticia
// Fórmulas alineadas con ProyeccionCrediticiaService
// src/pages/Agro/Productor/ProyeccionCrediticia/MetodologiaProyeccion.js
// =====================================================

import React, { useState } from "react"
import { Card, CardBody, CardHeader, Collapse, Button } from "reactstrap"
import SiembrasVigentesNota from "../SiembrasVigentesNota"

const P = {
  verde: { text: "#1a7431", border: "#34a853" },
  ambar: { text: "#854d0e", border: "#f59e0b" },
  marron: { text: "#78350f", border: "#a16207" },
  teal: { text: "#115e59", border: "#0d9488" },
  violeta: { text: "#5b21b6", border: "#8b5cf6" },
  azul: { text: "#1e40af", border: "#3b82f6" },
  rojo: { text: "#7f1d1d", border: "#b91c1c" },
  gris: { text: "#334155", border: "#64748b" },
}

const MetodologiaProyeccion = () => {
  const [show, setShow] = useState(false)

  return (
    <div className="mb-3 text-muted">
      <Button
        color="light"
        size="sm"
        outline
        onClick={() => setShow(!show)}
        className="border"
      >
        <i className="mdi mdi-help-circle-outline me-1" />
        {show ? "Ocultar metodología" : "¿Cómo se calcula?"}
      </Button>

      <Collapse isOpen={show}>
        <Card className="mt-2 mb-0 border-0 shadow-sm overflow-hidden">
          <div style={{ height: "3px", background: P.azul.border }} />
          <CardHeader className="bg-transparent py-2">
            <span className="fw-semibold">
              Metodología de la proyección crediticia
            </span>
            <small className="text-muted d-block">
              Los parámetros se configuran en{" "}
              <strong>Administración &gt; Proyección Crediticia</strong>
            </small>
          </CardHeader>
          <CardBody style={{ fontSize: "0.88rem" }}>
            <SiembrasVigentesNota />
            <Seccion
              titulo="1. Ingreso agrícola proyectado"
              color={P.verde.text}
              border={P.verde.border}
            >
              <p>
                Para cada siembra activa se calcula producción × precio,
                ajustando el rendimiento por NDVI satelital:
              </p>
              <Formula>
                <strong>Rendimiento ajustado</strong> = Rendimiento base ×
                Factor NDVI{"\n"}
                <strong>Producción (tn)</strong> = Superficie (ha) × Rendimiento
                ajustado (qq/ha) ÷ 10{"\n"}
                <strong>Ingreso bruto</strong> = Producción × Precio (USD/tn)
                {"\n"}
                <strong>Costo total</strong> = Sup. × (Costos directos +
                G.Comercialización + Arrend. + Admin. + Seguro){"\n"}
                <strong>Margen bruto</strong> = Ingreso bruto − Costo total
              </Formula>
              <div className="d-flex flex-wrap gap-1 mb-1">
                <NdviBadge
                  label="Excelente"
                  desc=">0.6"
                  factor="×1.10"
                  color={P.verde}
                />
                <NdviBadge
                  label="Bueno"
                  desc="0.4-0.6"
                  factor="×1.00"
                  color={P.azul}
                />
                <NdviBadge
                  label="Regular"
                  desc="0.2-0.4"
                  factor="×0.75"
                  color={P.ambar}
                />
                <NdviBadge
                  label="Malo"
                  desc="<0.2"
                  factor="×0.50"
                  color={P.rojo}
                />
              </div>
              <Ref>
                Factores NDVI y umbrales en Proyección Crediticia &gt; Ajuste
                por NDVI. Costos en Valores Agro.
              </Ref>
            </Seccion>

            {/* BDSJEA-165: la metodologia explicaba la formula pero no de
                donde sale el rendimiento base, que es el dato con mas peso del
                calculo. Con la cadena de seis niveles y la prioridad
                configurable, no declararlo dejaba el numero sin explicacion. */}
            <Seccion
              titulo="2. De dónde sale el rendimiento base"
              color={P.teal.text}
              border={P.teal.border}
            >
              <p className="mb-1">
                Se resuelve por niveles. El sistema usa el primero que tiene
                dato y deja registrado en los supuestos del informe cuál fue:
              </p>
              <ol className="mb-2" style={{ paddingLeft: "1.2rem" }}>
                <li>
                  <strong>Declarado en la siembra</strong>, si lo cargó una
                  persona. Un valor sugerido por el sistema que nadie editó no
                  cuenta como declaración y la cadena sigue.
                </li>
                <li>
                  <strong>Rendimiento real</strong> de la campaña anterior.
                </li>
                <li>
                  <strong>Serie oficial del MAGyP</strong> — promedio de las
                  últimas campañas del departamento del establecimiento. Si el
                  departamento no tiene dato, cae al total provincial.
                </li>
                <li>
                  <strong>Valores Agro</strong>, por zona y cultivo. Es el nivel
                  que resuelve en vid, olivo y frutales, donde no hay serie
                  oficial.
                </li>
                <li>
                  <strong>Parámetro por cultivo</strong>, sin distinguir zona.
                </li>
                <li>
                  <strong>Rendimiento por defecto.</strong> El informe lo
                  declara como valor genérico.
                </li>
              </ol>
              <p className="mb-1">
                El orden entre los niveles 3 y 4 se invierte con el parámetro{" "}
                <strong>Origen Rendimiento Preferido</strong>. Por defecto gana
                la serie oficial, que es la única fuente externa citable en el
                informe.
              </p>
              <Ref>
                La zona del establecimiento se deduce de su departamento, y de
                la provincia como respaldo. El costo por hectárea se resuelve
                sobre esa misma zona, para que ingreso y costo de una línea no
                salgan de lugares distintos.
              </Ref>
            </Seccion>

            <Seccion
              titulo="3. Fecha de cosecha estimada"
              color={P.ambar.text}
              border={P.ambar.border}
            >
              <p className="mb-1">Se estima en orden de prioridad:</p>
              <ol className="mb-0" style={{ paddingLeft: "1.2rem" }}>
                <li>
                  <strong>Fecha declarada</strong> por el productor.
                </li>
                <li>
                  <strong>Fecha siembra + ciclo</strong> típico del cultivo
                  según zona (calendario agrícola).
                </li>
                <li>
                  <strong>Ventana de cosecha</strong> típica de la zona si no
                  hay fecha de siembra.
                </li>
              </ol>
              <Ref>
                Calendario agrícola por zona y cultivo. Ciclo del cultivo en
                Administración &gt; Cultivos.
              </Ref>
            </Seccion>

            <Seccion
              titulo="4. Ingreso ganadero proyectado"
              color={P.marron.text}
              border={P.marron.border}
            >
              <Formula>
                <strong>Valor cabeza</strong> = Peso Promedio (kg) × Precio/kg
                (USD) por categoría{"\n"}
                <strong>Valuación rodeo</strong> = Σ (Cabezas × Valor cabeza)
                {"\n"}
                <strong>Ingreso anual</strong> = Valuación × % venta anual −
                Gastos venta (%){"\n"}
                <strong>Costos</strong> = Σ (Cabezas × (G.Productivos + G.Cría))
              </Formula>
              <p className="mb-1">Para establecimientos tamberos se suma:</p>
              <Formula>
                <strong>Ingreso tambo</strong> = Vacas × Lts/día × 365 × Precio
                litro{"\n"}
                <strong>Costos tambo</strong> = % Tambero + Gasto anual/vaca +
                Reposición + Compra{"\n"}
                <strong>Margen tambo</strong> = Ingreso − Costos
              </Formula>
              <Ref>
                Valores Ganadería + Valores Generales (tambo). Detección tambo
                por actividad del establecimiento.
              </Ref>
            </Seccion>

            <Seccion
              titulo="5. Capacidad de pago"
              color={P.violeta.text}
              border={P.violeta.border}
            >
              <Formula>
                <strong>Margen neto</strong> = Ingreso total (agrícola +
                ganadero + tambo) − Costos totales{"\n"}
                <strong>Capacidad pago anual</strong> = Margen neto × Factor
                capacidad (configurable){"\n"}
                <strong>Cuota máxima mensual</strong> = Capacidad anual ÷ 12
              </Formula>
              <p
                className="mb-0"
                style={{ fontSize: "0.82rem", color: P.gris.text }}
              >
                El factor (defecto 70%) reserva el resto para contingencias
                climáticas y reinversión productiva.
              </p>
              <Ref>Proyección Crediticia &gt; Capacidad de pago.</Ref>
            </Seccion>

            <Seccion
              titulo="6. Dictamen crediticio"
              color={P.azul.text}
              border={P.azul.border}
            >
              <div className="d-flex flex-wrap gap-2 mb-2">
                <DictBadge
                  tipo="FAVORABLE"
                  desc="Ratio cuota/ingreso dentro del máximo"
                  color={P.verde}
                />
                <DictBadge
                  tipo="CONDICIONAL"
                  desc="Monto excede capacidad o ratio alto"
                  color={P.ambar}
                />
                <DictBadge
                  tipo="DESFAVORABLE"
                  desc="Sin margen suficiente"
                  color={P.rojo}
                />
              </div>
              <p
                className="mb-0"
                style={{ fontSize: "0.82rem", color: P.gris.text }}
              >
                Sugiere monto máximo, plazo alineado con cosecha (+ meses de
                comercialización) y garantía (prenda del cultivo principal).
              </p>
              <Ref>
                Ratio máximo y plazos en Proyección Crediticia &gt; Dictamen.
              </Ref>
            </Seccion>

            <Seccion
              titulo="7. Nivel de confianza"
              color={P.teal.text}
              border={P.teal.border}
            >
              <div className="d-flex flex-wrap gap-1 mb-1">
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#f0faf4",
                    color: P.verde.text,
                    border: `1px solid ${P.verde.border}40`,
                  }}
                >
                  ALTO — Fecha siembra + NDVI reciente + rendimiento declarado
                </span>
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#fefce8",
                    color: P.ambar.text,
                    border: `1px solid ${P.ambar.border}40`,
                  }}
                >
                  MEDIO — Falta 1 de 3 factores
                </span>
                <span
                  className="px-2 py-0 rounded-pill"
                  style={{
                    fontSize: "0.72rem",
                    backgroundColor: "#fef2f2",
                    color: P.rojo.text,
                    border: `1px solid ${P.rojo.border}40`,
                  }}
                >
                  BAJO — Solo datos de calendario
                </span>
              </div>
            </Seccion>

            <Seccion
              titulo="8. Fuentes de datos"
              color={P.gris.text}
              border={P.gris.border}
              sinBorde
            >
              <div style={{ fontSize: "0.82rem", color: P.gris.text }}>
                <p className="mb-1">
                  <strong>Precios:</strong> BCR Rosario (pizarra) y MAGyP (FOB
                  oficial)
                </p>
                <p className="mb-1">
                  <strong>Rendimiento oficial:</strong> Estimaciones Agrícolas
                  del MAGyP, por cultivo, campaña y departamento. Cubre cereales
                  y oleaginosas; no publica vid, olivo ni frutales.
                </p>
                <p className="mb-1">
                  <strong>NDVI:</strong> Copernicus / Sentinel-2 (10m, cada 5
                  días)
                </p>
                <p className="mb-1">
                  <strong>Clima:</strong> serie diaria propia, con archivo
                  histórico de NASA POWER y alertas del Servicio Meteorológico
                  Nacional.
                </p>
                <p className="mb-1">
                  <strong>Calendario:</strong> Ciclos por zona basados en
                  INTA/BCR
                </p>
                <p className="mb-0">
                  <strong>Productor:</strong> Siembras, stock y establecimientos
                  del sistema EWAN
                </p>
              </div>
            </Seccion>
          </CardBody>
        </Card>
      </Collapse>
    </div>
  )
}

// Sub-componentes

const Seccion = ({ titulo, color, border, children, sinBorde }) => (
  <div
    className={sinBorde ? "mb-2" : "mb-3 pb-3"}
    style={sinBorde ? {} : { borderBottom: "1px solid #f1f5f9" }}
  >
    <div className="d-flex align-items-center gap-2 mb-2">
      <div
        style={{
          width: 3,
          height: 16,
          borderRadius: 2,
          backgroundColor: border || color,
        }}
      />
      <h6 style={{ color, marginBottom: 0 }}>{titulo}</h6>
    </div>
    {children}
  </div>
)

const Formula = ({ children }) => (
  <div
    className="rounded p-2 mb-2"
    style={{
      fontFamily: "monospace",
      fontSize: "0.82rem",
      whiteSpace: "pre-line",
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
    }}
  >
    {children}
  </div>
)

const Ref = ({ children }) => (
  <div className="mt-1 d-flex align-items-start gap-1">
    <i
      className="mdi mdi-cog-outline"
      style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 2 }}
    />
    <small style={{ fontSize: "0.73rem", color: "#64748b" }}>{children}</small>
  </div>
)

const NdviBadge = ({ label, desc, factor, color }) => (
  <span
    className="px-2 py-0 rounded"
    style={{
      fontSize: "0.7rem",
      backgroundColor: color.text + "08",
      color: color.text,
      border: `1px solid ${color.border}35`,
    }}
  >
    <strong>{label}</strong> ({desc}) {factor}
  </span>
)

const DictBadge = ({ tipo, desc, color }) => (
  <span
    className="px-2 py-1 rounded"
    style={{
      fontSize: "0.72rem",
      backgroundColor: color.text + "08",
      color: color.text,
      border: `1px solid ${color.border}35`,
    }}
  >
    <strong>{tipo}</strong> — {desc}
  </span>
)

export default MetodologiaProyeccion
